import { faArrowRotateLeft, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@feathr/components';

import { useImpersonationNavNotice } from './ImpersonationNavNotice.hooks';

import * as styles from './ImpersonationNavNotice.css';

/**
 * Nav notice (modeled on NavPreferenceToggle) shown while a sudoer is impersonating a user,
 * detected from the active JWT's `impersonator_id` claim. Reads "Impersonating" with a return
 * icon; the icon returns to the sudoer's own session (full-nav exchange, lands on the same page).
 */
function ImpersonationNavNotice(): JSX.Element | null {
  const { t } = useTranslation();
  const { isImpersonating, isReturning, returnToSession } = useImpersonationNavNotice();

  if (!isImpersonating) {
    return null;
  }

  return (
    <div className={styles.root}>
      <FontAwesomeIcon className={styles.icon} icon={faCircleExclamation} />
      <span className={styles.label}>{t('Impersonating')}</span>
      <Tooltip position={'right'} title={t('Return to your session')}>
        <button
          aria-label={t('Return to your session')}
          className={styles.returnButton}
          disabled={isReturning}
          onClick={returnToSession}
          type={'button'}
        >
          <FontAwesomeIcon icon={faArrowRotateLeft} spin={isReturning} />
        </button>
      </Tooltip>
    </div>
  );
}

export default observer(ImpersonationNavNotice);
